import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { useAppDispatch } from "@zenith/app/hooks";
import { CircularProgress } from "@mui/material";
import { DownloadIcon, SmsIcon } from "@zenith/assets/icons/components";
import {
  useNotificationContext,
  usePaymentsContext,
} from "@zenith/context/global-context/hooks";
import {
  EXPIRATION_DATE_FOR_BANK_TRANSFER_ITEM,
  EXPIRATION_DATE_FOR_PENDING_BANK_TRANSFER_ITEM,
  PAYMENTS_RECEIPT_DOWNLOAD_FILENAME,
} from "@zenith/data/constants";
import { emptyCart } from "@zenith/features/Payments/components";
import ShareReceiptComponent from "@zenith/features/ShareReceipt/components/share-receipt-component/share-receipt-component";
import { DASHBOARD, COLLECT_PAYMENT } from "@zenith/routes/paths";
import { OperationsApiService } from "@zenith/services/transactions";
import { cleanLocalStorageInfo, formatCurrency } from "@zenith/utils";
import { downloadFileFromBlob } from "@zenith/utils/Helpers/downloadFileUtil";
import { memo } from "react";
import { TransactionsParametersExportRecord } from "@zenith/features/Transactions/interfaces";
import { getPaymentMethodName } from "@zenith/utils/paymentMethodMapper";
import CheckCircle from "@zenith/assets/icons/CheckCircle";
import { ButtonComponent, ToastSnackbarComponent } from "@zenith/components";
import CopyToClipboard from "@zenith/components/ui/CopyToClipboard/CopyToClipboard";
import {
  formatTransactionDate,
  formatTransactionTime,
  shouldShow,
  isPaymentLinkCard,
  isPaymentLinkUSSD,
  isPaymentLinkQRCode,
  isPaymentLinkPayWithTransfer,
  isCashPayment,
  getCardType,
  truncateText,
} from "@zenith/utils/TransactionDisplayHelpers";
import { getStatusText } from "@zenith/utils/Helpers";
import QRComponent from "@zenith/features/Transactions/components/QRComponent/qr-component";

/* eslint-disable-next-line */
export interface PaymentSuccessfulPageProps {}

const PaymentSuccessfulPage = memo(function PaymentSuccessfulPage(
  props: PaymentSuccessfulPageProps
) {
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [hasCheckedContext, setHasCheckedContext] = useState(false);
  const [receivedData, setReceivedData] = useState<any>(null);

  const [isPaymentLink, setIsPaymentLink] = useState(false);

  const { paymentsContext, clearPaymentsContext, setPaymentsContext } =
    usePaymentsContext();

  const {
    totalAmount,
    transactionInfo,
    operationId,
    operationDetails,
    transactionId,
  } = paymentsContext;

  // `operationDetails` is whatever the last poll (or the previous page's
  // context snapshot) stored, which can still be missing the payer / bank /
  // session details when the merchant confirms the payment the moment it lands.
  // Hydrate the full operation record from the API so the success page always
  // shows complete details, no matter how fast the merchant confirmed.
  const [hydratedDetails, setHydratedDetails] = useState<any>(null);
  const details = hydratedDetails ?? operationDetails;
  const detailsRef = useRef<any>(null);
  detailsRef.current = details;
  const paymentsContextRef = useRef(paymentsContext);
  paymentsContextRef.current = paymentsContext;
  const hydrationKeyRef = useRef<string | null>(null);

  const { setOpen } = useNotificationContext();
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const stateOperationId = (location.state as { operationId?: string } | null)
    ?.operationId;

  const displayOperationId =
    receivedData?.operationId ||
    details?.operationId ||
    operationId ||
    stateOperationId;

  const displayDate =
    receivedData?.timestamp ||
    details?.createdAt ||
    transactionInfo?.transactionDate;
  const displayAmount =
    receivedData?.totalAmount || details?.totalAmount || totalAmount;

  const rawPaymentMethod =
    receivedData?.paymentMethod ||
    details?.paymentMethod ||
    transactionInfo?.paymentMethod;

  const displayPaymentMethod = getPaymentMethodName(rawPaymentMethod);

  const isCash = isCashPayment(rawPaymentMethod);
  const isUSSD =
    isPaymentLinkUSSD(rawPaymentMethod) || rawPaymentMethod === "USSD";
  const isQRCode =
    isPaymentLinkQRCode(rawPaymentMethod) || rawPaymentMethod === "QR_CODE";
  const isPayWithTransfer =
    isPaymentLinkPayWithTransfer(rawPaymentMethod) ||
    rawPaymentMethod === "PAY_WITH_TRANSFER";
  const isCard = isPaymentLinkCard(rawPaymentMethod);

  const status = details?.status || receivedData?.status;

  const payerAccountNumber = details?.payerAccount?.payerAccountNumber;
  const payerBank = details?.payerAccount?.bank?.bankName || "";
  const payerName = details?.payerAccount?.payerName;
  const detailsAny = details as any;

  const accountCredited =
    detailsAny?.accountCredited || transactionInfo?.accountNumber;
  const bankName =
    details?.inBankTransfer?.bankName || transactionInfo?.accountBank;
  const transferAccount =
    detailsAny?.transferAccount || details?.inBankTransfer?.accountNumber;
  const ussdString = details?.ussd?.operationCode;
  const sessionId = details?.sessionId;
  const displayTransactionId =
    receivedData?.transactionId || detailsAny?.transactionId || transactionId;
  const collectedBy = detailsAny?.collectorName || detailsAny?.collectedBy;
  const description = detailsAny?.description;
  const maskedPan = detailsAny?.payerAccount?.maskedPan;
  const cardType = getCardType(maskedPan);

  useEffect(() => {
    return () => {
      localStorage.removeItem("paymentStatus");
      localStorage.removeItem(EXPIRATION_DATE_FOR_PENDING_BANK_TRANSFER_ITEM);
      localStorage.removeItem(EXPIRATION_DATE_FOR_BANK_TRANSFER_ITEM);
    };
  }, []);

  useEffect(() => {
    const checkForPaymentLinkSuccess = () => {
      const successDataStr = localStorage.getItem("paymentLinkSuccessData");
      const successTime = localStorage.getItem("paymentLinkSuccessTime");

      if (successDataStr && successTime) {
        const successData = JSON.parse(successDataStr);
        const timeDiff = Date.now() - parseInt(successTime);

        if (timeDiff < 120000) {
          setReceivedData(successData);
          setIsPaymentLink(true);

          localStorage.removeItem("paymentLinkSuccessData");
          localStorage.removeItem("paymentLinkSuccessTime");

          if (successData.operationData) {
            setPaymentsContext(successData.operationData);
          }
        } else {
          localStorage.removeItem("paymentLinkSuccessData");
          localStorage.removeItem("paymentLinkSuccessTime");
        }
      }
    };

    if (!location.state) {
      checkForPaymentLinkSuccess();
    }
  }, [location.state, setPaymentsContext]);

  useEffect(() => {
    return () => {
      localStorage.removeItem("paymentLinkSuccessData");
      localStorage.removeItem("paymentLinkSuccessTime");
    };
  }, []);

  useEffect(() => {
    if (location.state) {
      setReceivedData(location.state);
      if (location.state.operationData) {
        setPaymentsContext(location.state.operationData);
      }
      setIsPaymentLink(location.state.isPaymentLink || false);
    }
  }, [location.state]);

  useEffect(() => {
    if (!hasCheckedContext) {
      const hasValidId =
        operationId || details?.operationId || displayOperationId;

      setPaymentsContext(paymentsContext);

      if (!hasValidId) {
        setShouldRedirect(true);
      }
      setHasCheckedContext(true);
    }
  }, [operationId, details, displayOperationId, hasCheckedContext]);

  // Self-heal the success page: for every entry path (fast manual confirm, the
  // pending pages, and any state-based navigation) re-read the operation from
  // the API until the full record is available, then publish it to the context.
  useEffect(() => {
    const locationState = location.state as { operationId?: string } | null;
    const recordId =
      operationId ||
      detailsRef.current?.operationId ||
      locationState?.operationId ||
      receivedData?.operationId;
    const isComplete = (record: any): boolean => {
      if (!record || !record.status || !record.totalAmount) return false;
      // Cash sales carry no payer; a static account sale's payer comes with the sale itself.
      if (isCash || rawPaymentMethod === "STATIC_ACCOUNT") return true;
      return Boolean(record.payerAccount?.payerName);
    };

    if (!recordId || hydrationKeyRef.current === recordId) return;
    hydrationKeyRef.current = recordId;

    if (isComplete(detailsRef.current)) return;

    const MAX_ATTEMPTS = 4;
    const RETRY_DELAY_MS = 1200;
    let attempts = 0;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const loadOperation = async (): Promise<void> => {
      attempts += 1;
      try {
        const response = await OperationsApiService.getOperationId(recordId);
        if (cancelled) return;

        const record = response?.data as any;

        if (record) {
          setHydratedDetails(record);

          if (isComplete(record) || attempts >= MAX_ATTEMPTS) {
            const currentContext = paymentsContextRef.current;
            setPaymentsContext({
              ...currentContext,
              operationDetails: record,
              paymentStatus: record.status ?? currentContext.paymentStatus,
              totalAmount: record.totalAmount ?? currentContext.totalAmount,
              transactionId:
                record.transactionId ?? currentContext.transactionId,
            });
          }
        }

        if (!isComplete(record) && attempts < MAX_ATTEMPTS) {
          retryTimer = setTimeout(loadOperation, RETRY_DELAY_MS);
        }
      } catch (error) {
        console.error("Failed to load operation details:", error);
        if (attempts < MAX_ATTEMPTS) {
          retryTimer = setTimeout(loadOperation, RETRY_DELAY_MS);
        }
      }
    };

    loadOperation();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationId, location.state]);

  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      navigate(DASHBOARD, { replace: true });
    };

    window.addEventListener("popstate", handleBackButton);

    return () => {
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [navigate]);

  if (shouldRedirect) {
    return <Navigate to={COLLECT_PAYMENT} replace />;
  }

  const handleGoToDashboard = () => {
    clearContext();
    navigate(DASHBOARD);
  };

  function clearContext(): void {
    cleanLocalStorageInfo();
    dispatch(emptyCart());
    clearPaymentsContext();
  }

  function downloadReceipt() {
    if (!displayOperationId) {
      console.error("Operation ID is missing");
      return;
    }

    setIsLoadingDownload(true);

    OperationsApiService.exportReceipt(displayOperationId)
      .then((response) => {
        if (response) {
          downloadFileFromBlob(
            response,
            PAYMENTS_RECEIPT_DOWNLOAD_FILENAME +
              displayOperationId.formatToShowID(),
            TransactionsParametersExportRecord.mediaType.PDF
          );

          notify(
            "Download Successful",
            "Receipt downloaded successfully",
            "success"
          );
        }
      })
      .catch((error: any) => {
        let message = "Unable to download receipt";

        if (error?.body) {
          try {
            if (typeof error.body === "string") {
              const parsed = JSON.parse(error.body);
              message = parsed?.message || message;
            } else if (typeof error.body === "object") {
              message = error.body?.message || message;
            }
          } catch (e) {
            message = error?.message || message;
          }
        } else if (error?.response?.body) {
          try {
            const parsed = JSON.parse(error.response.body);
            message = parsed?.message || message;
          } catch {
            console.log("");
          }
        } else {
          message = error?.message || message;
        }

        notify("Download Failed", message, "error");
      })
      .finally(() => setIsLoadingDownload(false));
  }

  return (
    <div className="mb-2 mt-12 flex min-h-[calc(100vh-128px)] max-w-[96%] flex-col justify-around">
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />
      <div className="flex w-full flex-col items-center gap-6 lg:flex-row lg:items-start lg:justify-center xl:gap-10">
        <div className="flex w-[668px] min-w-0 max-w-full flex-col items-center rounded-[20px] border border-neutral-200">
          <div className="flex w-full flex-col self-start p-8">
            <div className="mt-5 mb-9 flex justify-center">
              <CheckCircle width={56} height={56} />
            </div>
            <h2 className="text-charcoal-black mb-6 text-center text-xl font-medium leading-relaxed">
              Payment Successful
            </h2>

            {displayDate && (
              <>
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Date</p>
                  <p className="font-medium text-neutral-900">
                    {formatTransactionDate(displayDate)}
                  </p>
                </div>
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Time</p>
                  <p className="font-medium text-neutral-900">
                    {formatTransactionTime(displayDate)}
                  </p>
                </div>
              </>
            )}

            {rawPaymentMethod === "STATIC_ACCOUNT" && shouldShow(payerName) && (
              <div className="mb-2 flex justify-between">
                <p className="text-neutral-600">Payer's Name</p>
                <p className="font-medium text-neutral-900">{payerName}</p>
              </div>
            )}
            {!isCash && shouldShow(payerBank) && (
              <div className="mb-2 flex justify-between">
                <p className="text-neutral-600">Payer's Bank</p>
                <p className="font-medium text-neutral-900">{payerBank}</p>
              </div>
            )}
            {/*
          {!isCash && shouldShow(payerAccountNumber) && (
            <div className="mb-2 flex justify-between">
              <p className="text-neutral-600">Payer's Account</p>
              <CopyToClipboard text={payerAccountNumber} />
            </div>
          )} */}

            {!isCash && shouldShow(accountCredited) && (
              <>
                <hr className="my-4 border-t-1 border-neutral-200" />
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Merchant Account</p>
                  <CopyToClipboard text={accountCredited} />
                </div>
              </>
            )}

            {isPayWithTransfer && shouldShow(transferAccount) && (
              <div className="mb-2 flex justify-between">
                <p className="text-neutral-600">Transfer Account</p>
                <CopyToClipboard text={transferAccount} />
              </div>
            )}

            {shouldShow(bankName) &&
              (rawPaymentMethod === "IN_BANK_TRANSFER" ||
                isPayWithTransfer) && (
                <>
                  <hr className="my-4 border-t-1 border-neutral-200" />
                  <div className="mb-2 flex justify-between">
                    <p className="text-neutral-600">Bank Name</p>
                    <p className="font-medium text-neutral-900">{bankName}</p>
                  </div>
                </>
              )}

            {shouldShow(description) && (
              <>
                <hr className="my-4 border-t-1 border-neutral-200" />
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Description</p>
                  <p className="break-words font-medium text-neutral-900">
                    {truncateText(description)}
                  </p>
                </div>
              </>
            )}

            <hr className="my-4 border-t-1 border-neutral-200" />
            <div className="mb-2 flex justify-between">
              <p className="text-neutral-600">Transaction Type</p>
              <p className="font-medium text-neutral-900">
                {displayPaymentMethod}
              </p>
            </div>

            {isUSSD && shouldShow(ussdString) && (
              <div className="mb-2 flex justify-between">
                <p className="text-neutral-600">USSD String</p>
                <p className="font-medium text-neutral-900">{ussdString}</p>
              </div>
            )}

            {!isCash && shouldShow(displayTransactionId) && (
              <>
                <hr className="my-4 border-t-1 border-neutral-200" />
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Transaction ID</p>
                  <CopyToClipboard text={displayTransactionId} />
                </div>
              </>
            )}

            {!isCash && shouldShow(sessionId) && (
              <>
                <hr className="my-4 border-t-1 border-neutral-200" />
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Session ID</p>
                  <CopyToClipboard text={sessionId} />
                </div>
              </>
            )}

            {isCard && (
              <>
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Card Number</p>
                  <p className="font-medium text-neutral-900">
                    {maskedPan || "•••• •••• •••• ••••"}
                  </p>
                </div>
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Card Type</p>
                  <p className="font-medium text-neutral-900">{cardType}</p>
                </div>
              </>
            )}

            <hr className="my-4 border-t-1 border-neutral-200" />
            {shouldShow(status) && (
              <div className="mb-2 flex justify-between">
                <p className="text-neutral-600">Status</p>
                <p className="font-medium text-neutral-900">
                  {getStatusText(status)}
                </p>
              </div>
            )}
            {Boolean(displayAmount) && (
              <div className="mb-2 flex justify-between">
                <p className="text-neutral-600">
                  {isCash ? "Amount" : "Amount Charged"}
                </p>
                <p className="font-medium text-neutral-900">
                  {formatCurrency(displayAmount)}
                </p>
              </div>
            )}

            {isCash && shouldShow(collectedBy) && (
              <>
                <hr className="my-4 border-t-1 border-neutral-200" />
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Collected By</p>
                  <p className="font-medium capitalize text-neutral-900">
                    {collectedBy}
                  </p>
                </div>
              </>
            )}

            {!isPaymentLink && (
              <div className="mt-6 flex justify-center">
                <ButtonComponent
                  label="Go to Dashboard"
                  onClick={handleGoToDashboard}
                  id="goto-dashboard-button"
                  size="large"
                  type="primary"
                  fullWidth
                />
              </div>
            )}
          </div>
        </div>

        <div
          id="scan-receipt-card"
          className="flex w-[420px] min-w-0 max-w-full flex-col overflow-hidden rounded-[20px] border border-neutral-200"
        >
          <QRComponent operationId={displayOperationId} />
          <div className="details-buttons mt-6 flex flex-row items-center justify-around pt-2 pb-4">
            <button
              id="share-button"
              type="button"
              className="flex flex-col items-center justify-center p-2 transition-opacity hover:opacity-80"
              onClick={() => setShowEmail(true)}
            >
              <SmsIcon
                className="mb-1"
                fill={showEmail ? "#1A1C1F" : "#626262"}
              />
              <span
                className={`text-xs ${
                  showEmail ? "font-bold text-neutral-900" : "text-neutral-600"
                }`}
              >
                Share
              </span>
            </button>

            <button
              id="download-button"
              type="button"
              className="flex flex-col items-center justify-center p-2 transition-opacity hover:opacity-80"
              onClick={downloadReceipt}
              disabled={isLoadingDownload}
            >
              {isLoadingDownload ? (
                <div className="flex items-center justify-center">
                  <CircularProgress
                    className="text-primary-300"
                    size="1.2rem"
                  />
                </div>
              ) : (
                <>
                  <DownloadIcon className="mb-1" />
                  <span className="text-xs text-neutral-600">Download</span>
                </>
              )}
            </button>
          </div>

          {showEmail && displayOperationId && (
            <div className="px-4 pb-4">
              <hr className="mb-4" />
              <ShareReceiptComponent
                operationId={displayOperationId}
                notificationClass="mt-2"
                notify={notify}
                compact
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default PaymentSuccessfulPage;
